<template>
    <div
        ref="serviceElement"
        class="service"
        :class="{
            'pay-service': service.serviceType === ServiceType.Pay,
            'order-service': service.serviceType === ServiceType.Order,
            'has-open-bill': service.serviceType === ServiceType.Pay && hasOpenBill,
        }"
        @click="handleClick"
    >
        <div class="center">
            <div
                v-if="!showServiceImage"
                class="icon"
                :class="{
                    'kiosk-mode': kiosk.isKioskMode,
                }"
            >
                <i :class="serviceIcon"></i>
            </div>
            <div
                v-if="showServiceImage"
                class="image"
                :class="{
                    'kiosk-mode': kiosk.isKioskMode,
                }"
            >
                <img :src="serviceImage" alt="" />
            </div>
            <div
                class="lbl"
                :class="{
                    'kiosk-mode': kiosk.isKioskMode,
                }"
            >
                {{ serviceName }}
            </div>

            <div
                v-if="service.serviceType === ServiceType.Pay"
                class="bill-total-amount"
                :class="{ shown: showUnpaidAmount }"
            >
                {{ formattedUnpaidAmount }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ServicesControllerVue } from "@/app/controller/ServicesControllerVue";
    import { trans } from "@/app/localization/Trans";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { ServiceType } from "@/data/model/Enums";
    import type { IService } from "@/data/model/Service";
    import { useBillStore } from "@/data/stores/bill";
    import { useIdentityStore } from "@/data/stores/identity";
    import { Format } from "@/data/util/Format";
    import { translate } from "@/shared/localization/language";
    import { Ripple } from "@/shared/ui/Ripple";
    import { computed, onMounted, ref } from "vue";

    const bill = useBillStore();
    const identity = useIdentityStore();
    const kiosk = useKioskStore();

    const props = defineProps<{
        service: IService;
    }>();

    const hasOpenBill = computed(() => bill.unpaidAfterDiscounts > 0);
    const showUnpaidAmount = computed(() => identity.isWaiterInCurrentLocation);
    const formattedUnpaidAmount = computed(() => {
        if (!showUnpaidAmount.value) return "";
        return Format.toMoneyFormat(bill.unpaidAfterDiscounts);
    });

    const serviceElement = ref<HTMLElement>();

    const serviceName = computed(() => {
        let name = translate(props.service.name);
        if (name === undefined) {
            name = trans.enums.serviceType[ServiceType[props.service.serviceType]];
        }
        return name ?? "";
    });

    const serviceIcon = computed(() => {
        return props.service.iconClass === undefined
            ? Format.serviceIcon(props.service.serviceType)
            : props.service.iconClass;
    });

    const serviceImage = computed(() => props.service.imagePath);
    const showServiceImage = computed(() => serviceImage.value !== undefined);

    function handleClick() {
        ServicesControllerVue.onServiceClicked.dispatch(props.service);
    }

    onMounted(() => {
        if (serviceElement.value) {
            Ripple.bind($(serviceElement.value));
        }
    });

    defineExpose({
        serviceElement,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";

    .service {
        text-align: center;
        margin: auto;
        background-color: #fafafa;
        border: 1px solid #dcdcdc;
        border-radius: 28px;
        height: 100%;
        width: 100%;
        display: table;

        > .center {
            width: 100%;
            display: table-cell;
            vertical-align: middle;

            > .icon {
                height: 56px;

                // tiles are half the app width, so scale with the viewport
                // (portrait kiosk: vw is the narrow side)
                &.kiosk-mode {
                    font-size: clamp(80px, 9vw, 200px);
                    height: auto;

                    > i {
                        font-size: 1.4em !important;
                    }
                }

                > i {
                    font-size: 46px;
                    color: colors.$fontColor;
                    line-height: 1;
                }
            }

            > .lbl {
                padding: 6px 4px 0 4px;
                font-size: 16px;
                color: colors.$fontColor;
                line-height: 18px;

                &.kiosk-mode {
                    font-size: clamp(26px, 3.3vw, 70px);
                    line-height: 1.15;
                    font-weight: 600;
                    margin-top: clamp(34px, 4.3vw, 92px);
                }
            }

            > .image {
                // twice the kiosk icon glyph, about half the tile width
                &.kiosk-mode > img {
                    max-height: clamp(224px, 25.2vw, 560px);
                }

                > img {
                    max-width: 100%;
                    max-height: 80px;
                    object-fit: contain;
                }
            }
        }

        &.pay-service {
            &.has-open-bill {
                .icon > i {
                    color: colors.$red;
                }
            }

            .bill-total-amount {
                font-size: 16px;
                color: colors.$fontColor;
                font-weight: bold;
                padding: 4px 4px 0 4px;
                opacity: 0;
                transition: opacity 0.3s ease-out;

                &.shown {
                    opacity: 1;
                }
            }
        }
    }
</style>
